import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { DomPosition, type Chart } from 'klinecharts'
import { getTimeframeSeconds } from '../../utils/chartUtils'
import { formatCountdown, secondsLeft } from './barCountdown'

/** The bar the replay is forming: its start (unix s) and how much of it has played. */
export interface FormingProgress {
  time: number
  progress: number
}

interface Plate {
  top: number
  left: number
  width: number
  price: string
  countdown: string
  color: string
}

function samePlate(a: Plate | null, b: Plate | null): boolean {
  return (
    a === b ||
    (!!a &&
      !!b &&
      a.top === b.top &&
      a.left === b.left &&
      a.width === b.width &&
      a.price === b.price &&
      a.countdown === b.countdown &&
      a.color === b.color)
  )
}

function readPlate(chart: Chart, forming: FormingProgress | null, tfSec: number): Plate | null {
  if (!forming) return null
  const list = chart.getDataList()
  const last = list[list.length - 1]
  if (!last || last.timestamp !== forming.time * 1000) return null
  const px = chart.convertToPixel({ value: last.close }, { paneId: 'candle_pane', absolute: true })
  const y = Array.isArray(px) ? px[0]?.y : px.y
  const axis = chart.getSize('candle_pane', DomPosition.YAxis)
  const pane = chart.getSize('candle_pane', DomPosition.Main)
  if (y == null || !axis || !pane) return null
  // Off the price scale: the engine clips its own label there, and so does this one.
  if (y < pane.top || y > pane.top + pane.height) return null
  const mark = chart.getStyles().candle.priceMark.last
  const color = last.close > last.open ? mark.upColor : last.close < last.open ? mark.downColor : mark.noChangeColor
  return {
    top: y,
    left: axis.left,
    width: axis.width,
    price: last.close.toFixed(chart.getPriceVolumePrecision().price),
    countdown: formatCountdown(secondsLeft(tfSec, forming.progress)),
    color,
  }
}

/**
 * The last-price label while the walkthrough plays in real time: the price and, under it, the time left
 * on that bar, in the bar's colour (PROD-3178). It takes the engine label's seat on the price axis.
 */
export function WalkthroughPricePlate({
  chart,
  host,
  formingRef,
  timeframe,
  active,
}: {
  chart: Chart | null
  host: HTMLElement | null
  formingRef: React.RefObject<FormingProgress | null>
  timeframe: string
  active: boolean
}) {
  const [plate, setPlate] = useState<Plate | null>(null)

  useEffect(() => {
    if (!active || !chart) return undefined
    const tfSec = getTimeframeSeconds(timeframe)
    let raf = 0
    const frame = () => {
      const next = readPlate(chart, formingRef.current, tfSec)
      setPlate((prev) => (samePlate(prev, next) ? prev : next))
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [active, chart, formingRef, timeframe])

  const shown = active && plate !== null
  useEffect(() => {
    if (!chart || !shown) return undefined
    chart.setStyles({ candle: { priceMark: { last: { text: { show: false } } } } })
    return () => chart.setStyles({ candle: { priceMark: { last: { text: { show: true } } } } })
  }, [chart, shown])

  if (!active || !plate || !host) return null
  return createPortal(
    // Opaque backing, so the axis ticks behind the candle's translucent colour do not show through.
    <div
      data-testid="walkthrough-price-plate"
      className="pointer-events-none absolute z-10 -translate-y-1/2 rounded-sm bg-background"
      style={{ top: plate.top, left: plate.left, width: plate.width }}
    >
      <div
        className="rounded-sm px-1 text-right text-caption tabular-nums leading-tight text-white"
        style={{ backgroundColor: plate.color }}
      >
        <div>{plate.price}</div>
        <div className="opacity-80">{plate.countdown}</div>
      </div>
    </div>,
    host,
  )
}
